import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StorageService } from 'src/app/shared/services/storage.service';

@Component({
    selector: 'app-camera-capture',
    templateUrl: './camera-capture.component.html',
    styleUrls: ['./camera-capture.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CameraCaptureComponent implements AfterViewInit, OnDestroy, OnInit {
  data: any;
  @ViewChild('video') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  capturedImage: string | null = null;

  private mediaStream: MediaStream | null = null;
  private devices: MediaDeviceInfo[] = [];
  private currentDeviceId: string | null = null;
  private usingFrontCamera = true;

  constructor(private dialogRef: MatDialogRef<CameraCaptureComponent>, 
    @Inject(MAT_DIALOG_DATA) data: MatDialog, 
    public storageService: StorageService,
    private snackBar: MatSnackBar) {
    this.data = data;
  }

  ngOnInit(): void {
    if (this.isMobile()) {
      this.usingFrontCamera = false;
    }
  }

  async ngAfterViewInit(): Promise<void> {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      await this.loadCameras();
      await this.startCamera();
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Could not access the camera. Please allow permission and try again.');
    }
  }

  async loadCameras(): Promise<void> {
    const allDevices = await navigator.mediaDevices.enumerateDevices();
    this.devices = allDevices.filter(device => device.kind === 'videoinput');
  }

  async startCamera(): Promise<void> {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    const constraints: MediaStreamConstraints = {
      video: this.isMobile()
        ? { facingMode: this.usingFrontCamera ? 'user' : 'environment' }
        : {
            deviceId: this.currentDeviceId ? { exact: this.currentDeviceId } : undefined,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
      audio: false
    };

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.videoRef.nativeElement.srcObject = this.mediaStream;

      const videoTrack = this.mediaStream.getVideoTracks()[0];
      this.currentDeviceId = videoTrack.getSettings().deviceId || null;

      const capabilities = videoTrack.getCapabilities() as any;
      if (
        capabilities.focusMode &&
        Array.isArray(capabilities.focusMode) &&
        capabilities.focusMode.includes('continuous')
      ) {
        console.log('Device supports continuous focus mode, but skipping applyConstraints due to TS issues');
      } else {
        console.log('Continuous focus mode not supported');
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      alert('Could not access the camera. Please allow permission and try again.');
    }
  }

  switchCamera(): void {
    if (this.isMobile()) {
      this.usingFrontCamera = !this.usingFrontCamera;
    } else {
      if (this.devices.length < 2) return;
      const currentIndex = this.devices.findIndex(d => d.deviceId === this.currentDeviceId);
      const nextIndex = (currentIndex + 1) % this.devices.length;
      this.currentDeviceId = this.devices[nextIndex].deviceId;
    }
    this.startCamera();
  }

  isMobile(): boolean {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  capture(): void {
    const video = this.videoRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const videoAspect = videoWidth / videoHeight;
    const displayAspect = displayWidth / displayHeight;

    let sx = 0, sy = 0, sWidth = videoWidth, sHeight = videoHeight;

    if (videoAspect > displayAspect) {
      sWidth = videoHeight * displayAspect;
      sx = (videoWidth - sWidth) / 2;
    } else {
      sHeight = videoWidth / displayAspect;
      sy = (videoHeight - sHeight) / 2;
    }

    ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, displayWidth, displayHeight);
    this.capturedImage = canvas.toDataURL('image/jpeg', 0.95);
  }

  retake(): void {
    this.capturedImage = null;
    this.startCamera();
  }

  upload(): void {
    if (!this.capturedImage) return;

    const formData = new FormData();
    const blob = this.dataURItoBlob(this.capturedImage);

    const now = new Date();
    const [year, month, day] = now.toISOString().split('T')[0].split('-');
    const twoDigitYear = year.slice(2);
    const timeOnly = now.toTimeString().split(' ')[0].replace(/:/g, '');
    const dateTime = twoDigitYear + month + day + timeOnly;
    const parts = [
      this.data.projectName,
      this.data.sampleID,
      dateTime
    ].filter(part => part).join(' - ');
    const fileName = `${parts}.jpg`;

    formData.append('file', blob, fileName);
    this.storageService.uploadAttachments(this.data.projectID, formData).subscribe({
      next: (res) => {
        this.snackBar.open("Successfully uploaded", 'DONE', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['default-snackbar'],
        });
        this.dialogRef.close();
        this.close();
      }
    });
  }

  dataURItoBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  ngOnDestroy(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
